import React from 'react';
import './FournisseurDocumentDetailPage.css';

export const docTypeLabel = (type) => (
  type === 'bon_livraison' ? 'Bon de Livraison' : 'Bon de Commande'
);

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;

const formatQuantity = (val, isLineaire = false) => {
  const num = Number(val || 0);
  const formatted = num.toLocaleString('fr-FR', {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 3,
    maximumFractionDigits: 3
  });
  return `${formatted} ${isLineaire ? 'm.l.' : 'pièce(s)'}`;
};

const FournisseurDocumentDetailPage = ({
  doc,
  fournisseur,
  onBack,
  onDownloadPDF,
  onSendEmail,
  onRappelEmail,
  onWhatsApp,
  onConvertToBL,
  onImportPalettes,
  onDuplicate,
  onEdit,
  onValidate,
  onDelete
}) => {
  if (!doc) return null;

  const isBc = doc.type === 'bon_commande';
  const isBl = doc.type === 'bon_livraison';
  const isValide = doc.statut === 'valide';
  const canEditOrDelete = doc.statut === 'brouillon' || doc.statut === 'en_attente';
  const articles = Array.isArray(doc.articles) ? doc.articles : [];

  // Financial calculations
  const totalBrut = articles.reduce((acc, art) => {
    const qte = Number(art.quantite) || 0;
    const pu = Number(art.prixUnitaire) || 0;
    return acc + (qte * pu);
  }, 0);

  const totalRemise1 = articles.reduce((acc, art) => {
    const qte = Number(art.quantite) || 0;
    const pu = Number(art.prixUnitaire) || 0;
    const remise = Number(art.remise) || 0;
    return acc + (qte * pu * (remise / 100));
  }, 0);

  const totalRemise2Lines = articles.reduce((acc, art) => {
    const qte = Number(art.quantite) || 0;
    const pu = Number(art.prixUnitaire) || 0;
    const remise = Number(art.remise) || 0;
    const remise2 = Number(art.remise2) || 0;
    const baseR1 = (qte * pu) * (1 - remise / 100);
    return acc + (baseR1 * (remise2 / 100));
  }, 0);

  let globalRemise2Amt = 0;
  const r2Val = Math.max(0, Number(doc.remise2Val) || 0);
  if (doc.remise2Mode === 'global' && r2Val > 0) {
    const htBeforeGlobalR2 = totalBrut - totalRemise1 - totalRemise2Lines;
    if (doc.remise2Type === 'montant') {
      globalRemise2Amt = Math.min(htBeforeGlobalR2, r2Val);
    } else {
      globalRemise2Amt = htBeforeGlobalR2 * (Math.min(100, r2Val) / 100);
    }
  }

  const totalRemise2 = (doc.remise2Mode === 'produit' ? totalRemise2Lines : 0) + globalRemise2Amt;
  const totalRemise = totalRemise1 + totalRemise2;

  const totalHTNet = Number(doc.montantHT !== undefined ? doc.montantHT : (totalBrut - totalRemise));
  const fodecAmount = doc.fodecApplicable ? totalHTNet * ((Number(doc.tauxFodec) || 1) / 100) : 0;
  const baseTVA = totalHTNet + fodecAmount;
  const totalTVA = Number(doc.tva || 0);
  const timbreAmount = doc.timbreFiscalApplicable ? 1.000 : 0;
  const frais = Array.isArray(doc.fraisSupplementaires) ? doc.fraisSupplementaires : [];
  const totalFrais = frais.reduce((acc, f) => acc + (Number(f.prix) || 0), 0);
  const totalTTC = Number(doc.montantTTC || doc.montant || (totalHTNet + fodecAmount + totalTVA + totalFrais + timbreAmount));

  const sourceBcNumero = doc.bonCommandeRef?.numero || (typeof doc.bonCommandeRef === 'string' ? doc.bonCommandeRef : null);

  return (
    <div className="fdd-page">
      {/* ── Toolbar Header ── */}
      <div className="fdd-toolbar">
        <div className="fdd-toolbar-left">
          <button type="button" className="fdd-back-btn" onClick={onBack}>
            <i className="fas fa-arrow-left"></i>
            Retour aux documents
          </button>
          <div>
            <span className="fdd-kicker">
              <i className="fas fa-building"></i> {fournisseur?.raisonSociale || 'Fournisseur'}
            </span>
            <h1 className="fdd-title">
              {docTypeLabel(doc.type)} <span className="fdd-num">{doc.numero}</span>
            </h1>
          </div>
        </div>

        <div className="fdd-toolbar-actions">
          {/* Action PDF */}
          <button type="button" className="fdd-btn fdd-btn-pdf" title="Télécharger le PDF" onClick={onDownloadPDF}>
            <i className="fas fa-file-pdf"></i> PDF
          </button>

          {/* Action Email */}
          <button type="button" className="fdd-btn fdd-btn-email" title="Envoyer par Email" onClick={onSendEmail}>
            <i className="fas fa-envelope"></i> Email
          </button>

          {/* Action Rappel */}
          <button type="button" className="fdd-btn fdd-btn-rappel" title="Envoyer un rappel email" onClick={onRappelEmail}>
            <i className="fas fa-bell"></i> Rappel
          </button>

          {/* Action WhatsApp */}
          <button type="button" className="fdd-btn fdd-btn-whatsapp" title="Envoyer via WhatsApp" onClick={onWhatsApp}>
            <i className="fab fa-whatsapp"></i> WhatsApp
          </button>

          {/* Action Convertir en BL (si BC validé) */}
          {isBc && isValide && (
            <button type="button" className="fdd-btn fdd-btn-convert" title="Convertir en Bon de Livraison" onClick={onConvertToBL}>
              <i className="fas fa-truck"></i> Convertir en BL
            </button>
          )}

          {/* Action Importer Palettes (si BL validé) */}
          {isBl && isValide && (
            <button type="button" className="fdd-btn fdd-btn-palettes" title="Importer palettes Excel" onClick={onImportPalettes}>
              <i className="fas fa-file-excel"></i> Palettes
            </button>
          )}

          {/* Action Dupliquer (si BC) */}
          {isBc && (
            <button type="button" className="fdd-btn fdd-btn-duplicate" title="Dupliquer ce Bon de Commande" onClick={onDuplicate}>
              <i className="fas fa-copy"></i> Dupliquer
            </button>
          )}

          {/* Action Valider (si non validé) */}
          {!isValide && (
            <button type="button" className="fdd-btn fdd-btn-validate" title="Valider le document" onClick={onValidate}>
              <i className="fas fa-check-circle"></i> Valider
            </button>
          )}

          {/* Action Modifier (si modifiable) */}
          {canEditOrDelete && (
            <button type="button" className="fdd-btn fdd-btn-edit" title="Modifier le document" onClick={onEdit}>
              <i className="fas fa-edit"></i> Modifier
            </button>
          )}

          {/* Action Supprimer (si modifiable) */}
          {canEditOrDelete && (
            <button type="button" className="fdd-btn fdd-btn-delete" title="Supprimer le document" onClick={onDelete}>
              <i className="fas fa-trash-alt"></i> Supprimer
            </button>
          )}
        </div>
      </div>

      {/* ── Grid Row 1: Header Cards ── */}
      <div className="fdd-grid-2">
        {/* Card Document Info */}
        <div className="fdd-card">
          <div className="fdd-card-header">
            <h3><i className="fas fa-file-invoice"></i> Informations Document</h3>
            <span className={`fdd-type-badge ${doc.type}`}>{docTypeLabel(doc.type)}</span>
          </div>
          <div className="fdd-info-list">
            <div className="fdd-info-row">
              <span className="fdd-label">Numéro de référence</span>
              <strong className="fdd-val-highlight">{doc.numero}</strong>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Date d'émission</span>
              <span className="fdd-val">{new Date(doc.dateCreation).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Statut du document</span>
              <span className={`fdd-statut-badge statut-${doc.statut}`}>{doc.statut}</span>
            </div>
            {sourceBcNumero && (
              <div className="fdd-info-row">
                <span className="fdd-label">Bon de Commande Source</span>
                <span className="fdd-source-badge">
                  <i className="fas fa-link"></i> {sourceBcNumero}
                </span>
              </div>
            )}
            <div className="fdd-info-row">
              <span className="fdd-label">Nombre d'articles</span>
              <span className="fdd-val">{articles.length} produit(s)</span>
            </div>
          </div>
        </div>

        {/* Card Fournisseur Info */}
        <div className="fdd-card">
          <div className="fdd-card-header">
            <h3><i className="fas fa-building"></i> Informations Fournisseur</h3>
          </div>
          <div className="fdd-info-list">
            <div className="fdd-info-row">
              <span className="fdd-label">Raison Sociale</span>
              <strong className="fdd-val">{fournisseur?.raisonSociale || '-'}</strong>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Contact Principal</span>
              <span className="fdd-val">{[fournisseur?.prenomContact, fournisseur?.nomContact].filter(Boolean).join(' ') || '-'}</span>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Adresse Email</span>
              <span className="fdd-val">{fournisseur?.email || 'Non renseignée'}</span>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Téléphone</span>
              <span className="fdd-val">{fournisseur?.telephone || 'Non renseigné'}</span>
            </div>
            <div className="fdd-info-row">
              <span className="fdd-label">Adresse & Ville</span>
              <span className="fdd-val">{[fournisseur?.adresse, fournisseur?.ville].filter(Boolean).join(', ') || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Grid Row 2: Detailed Product Table ── */}
      <div className="fdd-card fdd-products-card">
        <div className="fdd-card-header">
          <h3><i className="fas fa-boxes"></i> Tableau des Produits & Articles ({articles.length})</h3>
        </div>

        {articles.length === 0 ? (
          <div className="fdd-empty-products">
            <i className="fas fa-box-open"></i>
            <span>Aucun produit associé à ce document.</span>
          </div>
        ) : (
          <div className="fdd-table-container">
            <table className="fdd-table">
              <thead>
                <tr>
                  <th>Produit & Référence</th>
                  <th>Gestion & Découpes</th>
                  <th className="text-center">Quantité</th>
                  <th className="text-right">Prix U. HT</th>
                  <th className="text-center">Remise</th>
                  <th className="text-center">TVA</th>
                  <th className="text-right">Total HT</th>
                  <th className="text-right">Total TTC</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article, index) => {
                  const produit = article.produitId;
                  const isLineaire = Boolean(article.lineaire || produit?.modeGestionStock === 'lineaire');
                  const ref = produit?.reference || '';
                  const qte = Number(article.quantite) || 0;
                  const pu = Number(article.prixUnitaire) || 0;
                  const remise = Number(article.remise) || 0;
                  const remise2 = Number(article.remise2) || 0;
                  let remiseText = '-';
                  if (remise > 0 && remise2 > 0) {
                    remiseText = `${remise}% / ${remise2}%`;
                  } else if (remise > 0) {
                    remiseText = `${remise}%`;
                  } else if (remise2 > 0) {
                    remiseText = `0% / ${remise2}%`;
                  }

                  const tvaRate = Number(article.tva) || 19;
                  const lineHT = Number(article.montantHT || (qte * pu * (1 - remise / 100) * (1 - remise2 / 100)));
                  const lineTTC = Number(article.montantTTC || (lineHT * (1 + tvaRate / 100)));

                  const decoupes = Array.isArray(article.decoupes) && article.decoupes.length > 0
                    ? article.decoupes
                    : (Array.isArray(produit?.decoupesLineaires) ? produit.decoupesLineaires : []);

                  return (
                    <tr key={article._id || index}>
                      <td>
                        <div className="fdd-prod-name">{article.designation || produit?.nomProduit || 'Produit'}</div>
                        {ref && <div className="fdd-prod-ref">Réf: {ref}</div>}
                        {article.specifications && (
                          <div className="fdd-prod-spec"><i className="fas fa-info-circle"></i> {article.specifications}</div>
                        )}
                      </td>
                      <td>
                        {isLineaire ? (
                          <div className="fdd-lineaire-info">
                            <span className="fdd-mode-chip lineaire"><i className="fas fa-ruler-horizontal"></i> Linéaire</span>
                            {decoupes.length > 0 && (
                              <div className="fdd-cuts-list">
                                {decoupes.map((cut, idx) => {
                                  const pieces = Math.max(1, parseInt(cut.nombrePieces, 10) || 1);
                                  const long = Number(cut.longueurPieceMetre) || 0;
                                  const m = Number(cut.metres) || (pieces * long);
                                  return (
                                    <span key={idx} className="fdd-cut-pill">
                                      {pieces} p. × {long.toLocaleString('fr-FR')} m = {m.toLocaleString('fr-FR')} m
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="fdd-mode-chip unite"><i className="fas fa-cube"></i> Unité</span>
                        )}
                      </td>
                      <td className="text-center font-bold">
                        {formatQuantity(qte, isLineaire)}
                      </td>
                      <td className="text-right">
                        {formatCurrency(pu)}
                      </td>
                      <td className="text-center">
                        {remiseText}
                      </td>
                      <td className="text-center">
                        {tvaRate}%
                      </td>
                      <td className="text-right font-semibold">
                        {formatCurrency(lineHT)}
                      </td>
                      <td className="text-right font-bold text-emerald">
                        {formatCurrency(lineTTC)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Grid Row 3: Notes & Financial Summary ── */}
      <div className="fdd-grid-2">
        {/* Left: Notes & Conditions */}
        <div className="fdd-card">
          <div className="fdd-card-header">
            <h3><i className="fas fa-sticky-note"></i> Conditions & Notes</h3>
          </div>
          <div className="fdd-details-stack">
            {doc.description && (
              <div className="fdd-detail-block">
                <strong>Description du document :</strong>
                <p>{doc.description}</p>
              </div>
            )}
            {doc.conditions && (
              <div className="fdd-detail-block">
                <strong>Conditions de paiement / livraison :</strong>
                <p>{doc.conditions}</p>
              </div>
            )}
            {doc.notes && (
              <div className="fdd-detail-block">
                <strong>Notes internes :</strong>
                <p>{doc.notes}</p>
              </div>
            )}

            {/* Extra Fees */}
            {frais.length > 0 && (
              <div className="fdd-frais-block">
                <strong>Frais supplémentaires :</strong>
                <ul>
                  {frais.map((f, i) => (
                    <li key={i}>
                      <span>{f.label || 'Frais'}</span>
                      <strong>{formatCurrency(f.prix)}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!doc.description && !doc.conditions && !doc.notes && frais.length === 0 && (
              <p className="fdd-muted">Aucune note particulière spécifiée sur ce document.</p>
            )}
          </div>
        </div>

        {/* Right: Financial Totals Breakdown */}
        <div className="fdd-card fdd-summary-card">
          <div className="fdd-card-header">
            <h3><i className="fas fa-calculator"></i> Synthèse Financière</h3>
          </div>
          <dl className="fdd-summary-dl">
            <div className="fdd-summary-row">
              <dt>Total HT Brut</dt>
              <dd>{formatCurrency(totalBrut)}</dd>
            </div>
            {totalRemise > 0 && (
              <div className="fdd-summary-row remise">
                <dt>Total Remises</dt>
                <dd>-{formatCurrency(totalRemise)}</dd>
              </div>
            )}
            {totalRemise2 > 0 && (
              <div className="fdd-summary-row remise" style={{ fontSize: '0.85rem', color: '#64748b' }}>
                <dt style={{ paddingLeft: '12px' }}>dont 2ᵉ remise ({doc.remise2Mode === 'global' ? 'Globale' : 'Par produit'})</dt>
                <dd>-{formatCurrency(totalRemise2)}</dd>
              </div>
            )}
            <div className="fdd-summary-row">
              <dt>Total HT Net</dt>
              <dd>{formatCurrency(totalHTNet)}</dd>
            </div>
            {doc.fodecApplicable && fodecAmount > 0 && (
              <>
                <div className="fdd-summary-row">
                  <dt>FODEC ({doc.tauxFodec || 1} %)</dt>
                  <dd>{formatCurrency(fodecAmount)}</dd>
                </div>
                <div className="fdd-summary-row">
                  <dt>Base TVA</dt>
                  <dd>{formatCurrency(baseTVA)}</dd>
                </div>
              </>
            )}
            <div className="fdd-summary-row">
              <dt>Total TVA</dt>
              <dd>{formatCurrency(totalTVA)}</dd>
            </div>
            {doc.timbreFiscalApplicable && timbreAmount > 0 && (
              <div className="fdd-summary-row">
                <dt>Timbre fiscal</dt>
                <dd>{formatCurrency(timbreAmount)}</dd>
              </div>
            )}
            {isBl && totalFrais > 0 && (
              <div className="fdd-summary-row">
                <dt>Frais supplémentaires</dt>
                <dd>{formatCurrency(totalFrais)}</dd>
              </div>
            )}
            <div className="fdd-summary-row total-ttc">
              <dt>TOTAL TTC</dt>
              <dd>{formatCurrency(totalTTC)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ── Grid Row 4: Document History Timeline ── */}
      {Array.isArray(doc.historique) && doc.historique.length > 0 && (
        <div className="fdd-card">
          <div className="fdd-card-header">
            <h3><i className="fas fa-history"></i> Journal & Historique des Actions</h3>
          </div>
          <div className="fdd-timeline">
            {doc.historique.map((item, idx) => (
              <div key={idx} className="fdd-timeline-item">
                <div className="fdd-timeline-dot"></div>
                <div className="fdd-timeline-content">
                  <div className="fdd-timeline-meta">
                    <span className="fdd-timeline-action">{item.action}</span>
                    <span className="fdd-timeline-date">{new Date(item.date).toLocaleString('fr-FR')}</span>
                  </div>
                  <p className="fdd-timeline-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FournisseurDocumentDetailPage;
